<template>
  <div class="practical-exam">
    <div class="practical-exam__header">
      <h2 class="practical-exam__title">Hands-on Lab</h2>
      <div class="practical-exam__steps">
        Start Lab <ArrowRightIcon /> Complete Objectives
        <ArrowRightIcon /> Submit Lab
      </div>
    </div>
    <div class="practical-exam__wrapper-lab">
      <div class="practical-exam__lab" v-if="!loadingLab">
        <PTACardCurrentSession
          :currentLab="examLab"
          :currentSession="currentSession"
          :setLabStatus="() => {}"
          :startWithPrimary="labNotSubmitted"
          :startDisabled="loadingVerification"
          :context="context"
          :mixpanelEventPayload="mixpanelEventPayload"
          @close-modal="$emit('close-modal')"
          @status-has-changed="changeCurrentLabStatus"
        />
      </div>
      <div class="practical-exam__lab" v-else>
        <CertificationLabSkel />
      </div>
      <div class="practical-exam__objectives">
        <div v-if="labNotSubmitted" class="practical-exam__objectives__wrapper">
          <div class="practical-exam__objectives__header">
            <div class="practical-exam__objectives__header--left">
              <h3 class="practical-exam__objectives__title">Objectives</h3>
              <p class="practical-exam__objectives__subtitle">
                Each objective has a set number of points associated with it.
              </p>
            </div>
            <div class="practical-exam__objectives__header--right">
              Minimum score required:
              <span class="practical-exam__pill">
                {{ examLab.min_points_to_pass || 0 }}
                {{ examLab.min_points_to_pass === 1 ? 'POINT' : 'POINTS' }}
              </span>
            </div>
          </div>
          <div
            class="practical-exam__objectives__objective"
            v-for="(objective, index) in objectives"
            :key="index"
          >
            <div class="practical-exam__objectives__objective__content">
              <span class="practical-exam__pill"
                >{{ objective.points }}
                {{ objective.points === 1 ? 'POINT' : 'POINTS' }}</span
              >
              <span v-html="objective.description_html"></span>
            </div>
          </div>
        </div>
        <div
          v-else
          class="practical-exam__objectives__wrapper practical-exam__objectives__submitted"
        >
          <h3 class="practical-exam__objectives__title">
            Your Hands-on Lab has been submitted!
          </h3>
          <p class="practical-exam__objectives__subtitle">
            You submitted your Hands-on Lab on {{ userLabAttemptDate }}
          </p>
        </div>
      </div>
      <div v-if="labIsRunning" class="practical-exam__warning">
        <InfoIcon /> You can submit your lab&nbsp;<strong>ONLY ONCE</strong>.
        Upon submitting your lab, your solution will be evaluated.
      </div>
      <div
        class="practical-exam__submit"
        v-if="labIsRunning || !labNotSubmitted"
      >
        <div class="practical-exam__submit__messages">
          <span
            class="practical-exam__submit__pill"
            :class="
              (loadingVerification || !labNotSubmitted) &&
                'practical-exam__submit__pill--purple'
            "
            ><LoadingSmallSpinner v-if="loadingVerification" />{{
              labSubmitStatus
            }}</span
          >
          <span>{{ labSubmitStatusText }}</span>
        </div>

        <button
          v-if="labNotSubmitted"
          class="btn btn--primary"
          @click="submitLabForVerify"
          :disabled="!labIsRunning || loadingVerification"
        >
          Submit lab
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import { ArrowRightIcon, InfoIcon } from 'vue-feather-icons'
import { mapState } from 'vuex'
import { format } from 'date-fns'
import LoadingSmallSpinner from '@/components/loaders/LoadingSmallSpinner.vue'
import CertificationLabSkel from '@/components/loaders/skeletons/certifications/CertificationLabSkel.vue'
import PTACardCurrentSession from '@/components/modals/lab-modal/PTACardCurrentSession.vue'
import CONSTANTS from '@/constants'

export default {
  name: 'PracticalExam',
  components: {
    PTACardCurrentSession,
    ArrowRightIcon,
    InfoIcon,
    LoadingSmallSpinner,
    CertificationLabSkel,
  },
  props: {
    attempt: {
      type: Object,
      required: true,
    },
    userLabAttempt: {
      type: Object,
      required: false,
      default: () => {
        return null
      },
    },
    context: {
      type: String,
      required: true,
    },
    certification: {
      type: Object,
      required: true,
    },
  },
  data: () => {
    return {
      objectiveCheck: [],
      loadingVerification: false,
      currentLabStatus: CONSTANTS.LABS.SESSION_STATUS.IDDLE,
      loadingLab: false,
    }
  },
  computed: {
    ...mapState({
      examLab: state => state.labs.currentLab,
      currentSession: state => state.labs.currentLabSession,
    }),
    mixpanelEventPayload() {
      const {
        id: certification_id,
        name: certification_name,
      } = this.certification
      const { id: lab_id, name: lab_name, lab_type } = this.examLab

      return {
        certification_id,
        certification_name,
        lab_id,
        lab_name,
        lab_type,
        lab_context: this.context,
        ...this.examLab,
      }
    },
    objectives() {
      return this.examLab.lab_objectives
    },
    labIsRunning() {
      return this.currentLabStatus === CONSTANTS.LABS.SESSION_STATUS.STARTED
    },
    labNotSubmitted() {
      return !this.userLabAttempt
    },
    labSubmitStatus() {
      if (this.userLabAttempt) return 'Submitted'
      if (this.loadingVerification) return 'Submitting'
      return 'Lab not submitted'
    },
    labSubmitStatusText() {
      if (this.userLabAttempt) return this.userLabAttemptDate
      if (this.loadingVerification) return 'This may take several minutes'
      return 'You will not be able to submit your exam without first submitting the hands-on lab.'
    },
    userLabAttemptDate() {
      if (!this.userLabAttempt) return ''
      return `${format(
        new Date(this.userLabAttempt.created),
        "LLLL d, yyyy 'at' hh:mm a"
      )}`
    },
  },
  methods: {
    async submitLabForVerify() {
      const attempt_id = this.attempt.id
      const {
        id: certification_id,
        name: certification_name,
      } = this.certification
      try {
        this.loadingVerification = true
        await this.$store.dispatch('certifications/verifyLab', attempt_id)
        this.sendMixpanelEvent(
          this.eventsConstants.CERTIFICATION_PRACTICAL_SUBMIT,
          {
            attempt_id,
            certification_id,
            certification_name,
            lab_id: this.examLab.id,
          }
        )
      } catch (error) {
        this.loadingVerification = false
      }
      this.loadingVerification = false
    },
    changeCurrentLabStatus(status) {
      this.currentLabStatus = status
    },
  },
  created() {
    this.objectiveCheck = []
    this.objectives.map(o => this.objectiveCheck.push(false))
  },
  async mounted() {
    const lab = this.attempt?.practice_lab
    if (lab) {
      this.loadingLab = true
      await this.$store.dispatch('labs/getLabById', lab)
      this.loadingLab = false
    }
  },
}
</script>
<style lang="scss" scoped>
.practical-exam {
  $this: &;
  margin-top: 20px;
  margin-bottom: 150px;
  flex: 1;
  &__title {
    font-weight: bold;
    font-size: 40px;
    line-height: 52px;
    margin: 0;
  }
  &__steps {
    font-size: 16px;
    line-height: 23px;
    display: flex;
    align-items: center;
    margin: 20px 0;
    svg {
      margin: 0 20px;
    }
  }
  &__header {
    margin-bottom: 30px;
  }
  &__objectives {
    $obj: &;
    padding: 30px;
    background: linear-gradient(
      111.02deg,
      rgba(18, 18, 18, 0.7) 3.86%,
      rgba(18, 18, 18, 0) 107.96%
    );
    border-radius: 10px;
    font-size: rem(16px);
    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 30px;
    }
    &__title {
      font-size: rem(20px);
      line-height: 26px;
      margin: 0;
    }
    &__objective {
      color: var(--light-blue-gray);
      margin-bottom: 20px;
      &__content {
        display: flex;
        align-items: center;
        align-content: flex-start;
        justify-content: flex-start;
        ::v-deep p {
          margin: 0;
        }
      }
      #{$this}__pill {
        margin-right: 20px;
        white-space: nowrap;
      }
    }
    &__submitted {
      text-align: center;
      #{$obj}__title {
        font-size: rem(24px);
        line-height: 32px;
        margin-bottom: 10px;
      }
      #{$obj}__subtitle {
        font-size: rem(16px);
        line-height: 24px;
        color: var(--light-blue-gray);
      }
    }
  }
  &__pill {
    padding: 9px 14px;
    font-size: 12px;
    line-height: 14px;
    background: linear-gradient(
      112.2deg,
      rgba(187, 188, 199, 0.4) -46.39%,
      rgba(187, 188, 199, 0) 112.38%
    );
    border-radius: 20px;
    border: 1px solid #3e3f43;
    color: var(--white);
  }
  &__wrapper-lab {
    background: linear-gradient(
      104.99deg,
      rgba(62, 63, 73, 0.7) -13.91%,
      rgba(62, 63, 73, 0) 100.48%
    );
    border-radius: 20px;
    padding: 30px;
  }
  &__lab {
    &--loading {
      display: flex;
    }
  }
  &__warning {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 55px 0;
    color: #88a9ff;
    svg {
      stroke: #88a9ff;
      margin-right: 10px;
    }
  }
  &__submit {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: rem(12px);
    border-top: 1px solid var(--tundora-gray);
    padding-top: 30px;
    &__messages {
      display: flex;
      align-items: center;
    }
    &__pill {
      display: flex;
      align-items: center;
      padding: 5px 8px;
      margin-right: 10px;
      color: #1e1e1e;
      background: var(--white);
      box-shadow: 0px 0px 10px rgba(167, 147, 246, 0.5);
      border-radius: 30px;
      ::v-deep .loading-small {
        margin-right: 5px;
        transform: scale(0.8);
        svg path {
          stroke: #1e1e1e;
        }
      }
      &--purple {
        background: #a793f6;
        box-shadow: 0px 0px 10px rgba(167, 147, 246, 0.5);
        border-radius: 30px;
      }
    }
  }
}
</style>
